import {
  AutoComplete,
  Button,
  Card,
  Form,
  Input,
  List,
  Select,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import Timezone from "timezones-list";
import { openNotification } from "../../../../Components/Notification";
import currencyService from "../../../../Service/currency.service";
import organizationService from "../../../../Service/organization.service";
import timezoneCountryService from "../../../../Service/timezone.country.service";
import timezoneService from "../../../../Service/timezone.service";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import "./OrgBasicDetails.scss";
import { industriesOptions } from "../../../../Utils/industry-constants";
import { Organization } from "../../../../Entities/Organization";
import AuthService from "../../../../Service/auth.service";

interface OrgBasicDetailsProps {
  onOnboardingFinish: () => void;
}

const OrgBasicDetails: React.FC<OrgBasicDetailsProps> = ({
  onOnboardingFinish,
}) => {
  const [form] = Form.useForm();
  const [currencies, setCurrencies] = useState<
    { currency_code: string; country: string }[]
  >([]);
  const [timezones, setTimezones] = useState<typeof Timezone>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const currencyData = await currencyService.getAllCurrencies();

        const formattedCurrencies = currencyData.map((item: any) => ({
          currency_code: item.currency_code,
          country: item.country,
        }));

        setCurrencies(formattedCurrencies);

        const initOrg = await organizationService.fetchData();
        setOrganization(initOrg);
        form.setFieldsValue({
          orgName: `${initOrg?.orgName}`,
        });

        const currentUser = AuthService.getInstance().getCurrentUser();
        if (currentUser?.email) {
          form.setFieldsValue({ supportEmail: currentUser.email });
        }

        //fetching json data
        const timezones = await timezoneService.getAllTimezones();
        const timezoneDataList =
          await timezoneCountryService.getTimezoneCountry();

        //setting timezone json list
        setTimezones(timezones);

        //getting timezone from the browser
        const timezone: any = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log("🚀 ~ init ~ timezone:", timezone);
        if (timezone && timezoneDataList[timezone]) {
          form.setFieldsValue({
            timeZone: `${timezone}`,
          });
          let countryCode: any = null;
          if (!timezoneDataList[timezone].c) {
            countryCode = timezoneDataList[timezoneDataList[timezone].a].c[0];
            console.log("🚀 ~ init ~ countryCode:", countryCode);
          } else {
            countryCode = timezoneDataList[timezone].c[0];
          }
          console.log("🚀 ~ init ~ countryCode:", countryCode);
          const reqCurrency = currencyData.find(
            (item) => item.country_code === countryCode
          );
          const countryName = reqCurrency?.country;

          console.log("🚀 ~ init ~ countryName:", countryName);
          const currencyInfo = formattedCurrencies.find(
            (item) => item.country === countryName
          );
          console.log("🚀 ~ init ~ currencyInfo:", currencyInfo);
          if (currencyInfo) {
            form.setFieldsValue({
              currency: `${currencyInfo.currency_code}-${currencyInfo.country}`,
            });
            console.log(form.getFieldValue("currency"));
          } else {
            console.log("Currency not found for the country:", countryName);
          }
        }
      } catch (error) {
        console.error("🚀 ~ init ~ error:", error);
      }
    };
    init();
  }, []);

  const timezoneOptions = timezones.map((timezone) => ({
    value: timezone.tzCode,
    label: (
      <List size="small">
        <List.Item style={{ padding: 0 }}>
          <List.Item.Meta
            title={
              <Typography.Text style={{ fontWeight: "normal" }}>
                {timezone.label}
              </Typography.Text>
            }
            description={
              <small>
                {timezone.name} {timezone.utc}
              </small>
            }
          />
        </List.Item>
      </List>
    ),
  }));

  const onFinish = async ({
    orgName: organizationName,
    orgWebsite: organizationWebsite,
    timeZone,
    currency,
    supportEmail,
    industry,
    description,
  }: {
    orgName?: string;
    orgWebsite?: string;
    timeZone?: string;
    currency?: string;
    supportEmail?: string;
    industry?: string;
    description?: string;
  }) => {
    if (!organization) {
      return;
    }

    const filteredCurrency = currency?.substring(0, 3);

    const unchanged =
      organization.orgName === (organizationName?.trim() ?? "") &&
      organization.orgWebsite === (organizationWebsite?.trim() ?? "") &&
      organization.timeZone === (timeZone ?? "") &&
      organization.currency === (filteredCurrency ?? "") &&
      organization.supportEmail === (supportEmail?.trim() ?? "") &&
      organization.industry === (industry?.trim() ?? "") &&
      organization.description === (description?.trim() ?? "");

    if (unchanged) {
      return;
    }

    try {
      organization.orgName = organizationName?.trim() ?? "";
      organization.orgWebsite = organizationWebsite?.trim() ?? "";
      organization.timeZone = timeZone ?? "";
      organization.currency = filteredCurrency ?? "";
      organization.supportEmail = supportEmail?.trim() ?? "";
      organization.industry = industry?.trim() ?? "";
      organization.description = description?.trim() ?? "";

      const response = await organizationService.updateOrganization(organization);
      if (response.status === 400) {
        openNotification("error", response?.message, "");
        return;
      }
      onOnboardingFinish();
    } catch (error) {
      console.error("Error updating organization details:", error);
      openNotification("error", "", "Failed to update organization details.");
    }
  };

  const groupCurrenciesByCode = (currencyData: any) => {
    return currencyData.reduce((acc: any, currency: any) => {
      const { currency_code } = currency;
      if (!acc[currency_code]) {
        acc[currency_code] = [];
      }
      acc[currency_code].push(currency);
      return acc;
    }, {});
  };

  const groupedCurrencies = groupCurrenciesByCode(currencies);

  return (
    <div className="org-details-container">
      <Card className="org-details-card" style={{ width: "100%" }}>
        {/* Header */}
        {/* <div className="form-header">
          <Typography.Title level={3} className="form-title">
            Complete Your Organization Setup
          </Typography.Title>
          <Typography.Paragraph className="form-subtitle">
            This information helps us personalize your hiring experience and
            ensure accurate billing
          </Typography.Paragraph>
        </div> */}

        <Form
          layout="vertical"
          form={form}
          className="full-width-form"
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
          autoComplete="off"
        >
          {/* ORG NAME & ORG WEBSITE */}
          <div className="form-group">
            <Form.Item
              label="Organization Name"
              name="orgName"
              rules={[
                {
                  required: true,
                  message: "Please enter your organization name!",
                },
              ]}
            >
              <Input
                placeholder="Enter your organization name"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Website Link (Optional)"
              name="orgWebsite"
              rules={[
                {
                  type: "url",
                  warningOnly: true,
                  message: "Please enter a valid URL",
                },
              ]}
            >
              <Input
                placeholder="https://yourcompany.com"
                size="large"
              />
            </Form.Item>
          </div>

          {/* TIME ZONE & CURRENCY */}
          <div className="form-group">
            <Form.Item
              label="Time Zone"
              name="timeZone"
              rules={[
                { required: true, message: "Please select your time zone!" },
              ]}
              extra="This helps us schedule interviews at convenient times for your team"
            >
              <AutoComplete
                options={timezoneOptions}
                placeholder="Select your timezone"
                onSelect={(value) => console.log("timezone", value)}
                filterOption={(inputValue, option) =>
                  option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                allowClear={true}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Currency"
              name="currency"
              rules={[
                {
                  required: true,
                  message: "Please select your preferred currency!",
                },
              ]}
              extra="This will be used for all billing and pricing displays"
            >
              <Select
                id="currency-select"
                showSearch
                placeholder="Choose your currency"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children
                    ?.toString()
                    .toLowerCase()
                    .includes(input.toLowerCase()) ?? false
                }
                size="large"
              >
                {Object.keys(groupedCurrencies).map((currencyCode) => (
                  <Select.OptGroup key={currencyCode} label={currencyCode}>
                    {groupedCurrencies[currencyCode].map((currency: any) => (
                      <Select.Option
                        key={`${currency.currency_code}-${currency.country}`}
                        value={`${currency.currency_code}-${currency.country}`}
                      >
                        {currency.country}&nbsp;(
                        <Typography.Paragraph style={{ display: "inline" }}>
                          {currency.currency_code}
                        </Typography.Paragraph>
                        )
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* SUPPORT EMAIL & INDUSTRY */}
          <div className="form-group">
            <Form.Item
              label="Support Email"
              name="supportEmail"
              rules={[
                {
                  required: true,
                  message: "Support email is required.",
                },
                {
                  type: "email",
                  message: "Please enter a valid email address.",
                },
              ]}
              extra="Candidates will use this email to contact your organization."
            >
              <Input
                placeholder="Enter your organization's support email"
                size="large"
              />
            </Form.Item>

            <Form.Item label="Industry (Optional)" name="industry" extra="  ">
              <AutoComplete
                options={industriesOptions}
                placeholder="Select an industry or type to add your own"
                onSelect={(value) => console.log("industry", value)}
                filterOption={(inputValue, option) =>
                  option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                allowClear={true}
                size="large"
              />
            </Form.Item>
          </div>

          {/* DESCRIPTION */}
          <Form.Item label="Description (Optional)" name="description" style={{ width: '100%', maxWidth: '1000px', margin: "0 auto"}}>
            <Input.TextArea
              placeholder={`Enter your organization's description\nE.g., We are an innovative organization in the XYZ industry, dedicated to delivering high-quality solutions that address complex challenges and create lasting impact for our clients and communities.`}
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>

          <Form.Item className="submit-section">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="submit-button"
              icon={<SafetyCertificateOutlined />}
            >
              Complete Setup & Continue
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default OrgBasicDetails;
